import { useEffect, useRef } from 'react';

/**
 * useExamProtection
 * Lightweight client-side protections for exam pages.
 * - Blocks copy/cut/paste/context menu/drag/select where possible
 * - Deters screenshots (PrintScreen key) by clearing clipboard and warns
 * - Monitors tab/window visibility/focus changes; counts violations
 * - Periodically autosaves via provided saveDraft callback
 *
 * Note: Browser limitations mean screenshots/screen recordings cannot be fully prevented.
 */
export default function useExamProtection({
  enabled = true,
  getState, // () => { answers, timeLeft }
  saveDraft, // async ({ answers, timeLeft }) => void
  onViolation, // (type, count) => void
  onAutoSubmit, // () => void
  autosaveMs = 15000,
  maxViolationsBeforeAutoSubmit = 3,
  warnBeforeUnload = true,
  // Relaxation controls
  countOnBlur = true,
  // Only count a violation if hidden/blurred for this long
  violationDebounceMs = 30000,
  violationThrottleMs = 5000,
}) {
  const violationCountRef = useRef(0);
  const autosaveTimerRef = useRef(null);
  const blurTimerRef = useRef(null);
  const visibilityTimerRef = useRef(null);
  const lastViolationAtRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const handleContextMenu = (e) => preventDefault(e);
    const handleCopy = (e) => preventDefault(e);
    const handleCut = (e) => preventDefault(e);
    const handlePaste = (e) => preventDefault(e);
    const handleDragStart = (e) => preventDefault(e);
    const handleSelectStart = (e) => preventDefault(e);

    const handleKeyDown = (e) => {
      // Block common shortcuts: copy/cut/paste/print/devtools/screenshot
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      const key = e.key?.toLowerCase();
      if (
        (ctrlOrMeta && (key === 'c' || key === 'x' || key === 'v' || key === 'p' || key === 's' || key === 'u')) ||
        key === 'printscreen' ||
        key === 'f12' ||
        (ctrlOrMeta && e.shiftKey && (key === 'i' || key === 'j'))
      ) {
        preventDefault(e);
        // Attempt to clear clipboard on PrintScreen (best-effort)
        if (key === 'printscreen') {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            try { navigator.clipboard.writeText('Screenshots are disabled during the exam.'); } catch (_) {}
          }
        }
        return false;
      }
      return true;
    };

    const markViolation = (type) => {
      const now = Date.now();
      if (now - lastViolationAtRef.current < violationThrottleMs) return; // throttle
      lastViolationAtRef.current = now;
      violationCountRef.current += 1;
      if (typeof onViolation === 'function') onViolation(type, violationCountRef.current);
      if (violationCountRef.current >= maxViolationsBeforeAutoSubmit && typeof onAutoSubmit === 'function') {
        onAutoSubmit();
      }
    };

    const startHiddenTimer = () => {
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current);
      visibilityTimerRef.current = setTimeout(() => {
        if (document.hidden) {
          markViolation('visibility-hidden');
        }
      }, violationDebounceMs);
    };

    const cancelHiddenTimer = () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) startHiddenTimer(); else cancelHiddenTimer();
    };

    const handleBlur = () => {
      if (!countOnBlur) return;
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
      blurTimerRef.current = setTimeout(() => {
        // Only count if still not focused and not visible
        const stillBlurred = typeof document.hasFocus === 'function' ? !document.hasFocus() : true;
        if (stillBlurred || document.hidden) {
          markViolation('window-blur');
        }
      }, violationDebounceMs);
    };
    const handleFocus = () => {
      // Cancel any pending timers when focus returns
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      cancelHiddenTimer();
    };

    // Mobile: pagehide when user switches app/tab; pageshow when they return
    const handlePageHide = () => {
      startHiddenTimer();
    };
    const handlePageShow = () => {
      handleFocus();
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('copy', handleCopy, true);
    document.addEventListener('cut', handleCut, true);
    document.addEventListener('paste', handlePaste, true);
    document.addEventListener('dragstart', handleDragStart, true);
    document.addEventListener('selectstart', handleSelectStart, true);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('visibilitychange', handleVisibilityChange, true);
    window.addEventListener('blur', handleBlur, true);
    window.addEventListener('focus', handleFocus, true);
    window.addEventListener('pagehide', handlePageHide, true);
    window.addEventListener('pageshow', handlePageShow, true);

    const handleBeforeUnload = (e) => {
      if (!warnBeforeUnload) return;
      // Prompt user if they attempt to leave/reload while in exam
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    if (warnBeforeUnload) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    // Autosave interval
    if (typeof saveDraft === 'function' && typeof getState === 'function') {
      autosaveTimerRef.current = setInterval(() => {
        try {
          const state = getState();
          if (state && typeof state === 'object') {
            saveDraft({ ...state });
          }
        } catch (_) {}
      }, autosaveMs);
    }

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('cut', handleCut, true);
      document.removeEventListener('paste', handlePaste, true);
      document.removeEventListener('dragstart', handleDragStart, true);
      document.removeEventListener('selectstart', handleSelectStart, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange, true);
      window.removeEventListener('blur', handleBlur, true);
      window.removeEventListener('focus', handleFocus, true);
      window.removeEventListener('pagehide', handlePageHide, true);
      window.removeEventListener('pageshow', handlePageShow, true);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [enabled, getState, saveDraft, onViolation, onAutoSubmit, autosaveMs, maxViolationsBeforeAutoSubmit, warnBeforeUnload, countOnBlur, violationDebounceMs, violationThrottleMs]);
}


